import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { distinctUntilChanged, forkJoin, Subscription, finalize, map } from 'rxjs';
import {
  ActionMenuComponent,
  ConfirmEditModalComponent,
  ErrorBlockComponent,
  FloatingActionFabComponent,
  HoverIdComponent,
  ListFilterShortcutDirective,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent,
  PagedFeedComponent,
} from '@clinic-platform/ui-components';

import { AuthService } from '../../../core/auth/auth.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { DeviceTypeQuickViewModalComponent } from '../../device-types/components/device-type-quick-view-modal.component';
import { DeviceTypesService } from '../../device-types/data-access/device-types.service';
import { DeviceTypeItem } from '../../device-types/data-access/device-types.models';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ProcedureTypeQuickViewModalComponent } from '../components/procedure-type-quick-view-modal.component';
import {
  ProcedureTypeItem,
  ProcedureTypeListSort,
  ProcedureTypePage,
  ProcedureTypeStatusFilter,
} from '../data-access/procedure-types.models';
import { ProcedureTypesService } from '../data-access/procedure-types.service';

type ProcedureTypeFilterState = {
  query: string;
  status: ProcedureTypeStatusFilter;
  deviceTypeId: string;
  sort: ProcedureTypeListSort;
};

type ProcedureTypeLoadResult = {
  page: ProcedureTypePage;
  deviceTypes: DeviceTypeItem[] | null;
};

@Component({
  selector: 'app-procedure-types-page',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    TranslatePipe,
    ActionMenuComponent,
    ConfirmEditModalComponent,
    ErrorBlockComponent,
    FloatingActionFabComponent,
    HoverIdComponent,
    ListFilterShortcutDirective,
    PageActionModalComponent,
    PageContextGuardComponent,
    PageShellComponent,
    PagedFeedComponent,
    CompanyContextRequiredComponent,
    PageHeaderComponent,
    DeviceTypeQuickViewModalComponent,
    ProcedureTypeQuickViewModalComponent,
  ],
  template: `
    <lib-page-shell
      libListFilterShortcut
      [libListFilterShortcut]="!isCompanyContextMissing"
      (libListFilterShortcutOpen)="openFiltersModal()"
    >
      <app-page-header
        [title]="procedureTypeLabelPlural"
        [subtitle]="'Company catalog of ' + procedureTypeLabelPluralLower + ' available for clinic assignment'"
      />

      <lib-page-context-guard [blocked]="isCompanyContextMissing">
        <app-company-context-required />
      </lib-page-context-guard>

      @if (!isCompanyContextMissing) {
        @if (actionErrorText) {
          <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
        }

        @if (successText) {
          <lib-error-block variant="success" [message]="successText" />
        }

        <lib-paged-feed
          [items]="items"
          [collectionTemplate]="procedureTypesTableTemplate"
          [loading]="isLoading"
          [loadingMore]="isLoadingMore"
          [hasMore]="hasMoreItems"
          [errorText]="errorText"
          [emptyText]="'No ' + procedureTypeLabelPluralLower + ' available yet.'"
          [loadMoreText]="'Load more ' + procedureTypeLabelPluralLower"
          loadingMoreText="Loading more..."
          retryText="Retry"
          (loadMore)="loadMoreProcedureTypes()"
          (retry)="refresh()"
        />

        <ng-template #procedureTypesTableTemplate let-pageItems="items">
          <section class="procedure-types-page__surface">
            <div class="procedure-types-page__surface-head">
              <div>
                <h3>{{ procedureTypeLabelPlural }}</h3>
                <p>{{ items.length }} loaded</p>
              </div>
            </div>

            <div class="procedure-types-page__table-wrap">
              <table class="procedure-types-page__table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Device Type</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @for (item of pageItems; track item.id) {
                    <tr class="procedure-types-page__table-row" (click)="openProcedureQuickView(item)">
                      <td>
                        <lib-hover-id [value]="item.id">
                          <span class="procedure-types-page__entity-name">{{ item.name }}</span>
                        </lib-hover-id>
                      </td>
                      <td>
                        @if (item.deviceTypeId) {
                          <button
                            type="button"
                            class="procedure-types-page__link-button"
                            (click)="openDeviceTypeQuickView(item.deviceTypeId); $event.stopPropagation()"
                          >
                            <lib-hover-id [value]="item.deviceTypeId">
                              {{ deviceTypeName(item.deviceTypeId) }}
                            </lib-hover-id>
                          </button>
                        } @else {
                          No device required
                        }
                      </td>
                      <td>{{ item.defaultDurationMinutes }} min</td>
                      <td>
                        <span
                          class="procedure-types-page__status-badge"
                          [class.procedure-types-page__status-badge--inactive]="!item.isActive"
                        >
                          {{ item.isActive ? ('Active' | t) : ('Inactive' | t) }}
                        </span>
                      </td>
                      <td>{{ item.createdAt ? (item.createdAt | date: 'dd.MM.yyyy HH:mm') : '—' }}</td>
                      <td>{{ item.updatedAt ? (item.updatedAt | date: 'dd.MM.yyyy HH:mm') : '—' }}</td>
                      <td class="procedure-types-page__actions-cell" (click)="$event.stopPropagation()">
                        <lib-action-menu [menuTemplate]="tableActionsMenu" buttonAriaLabel="Open procedure type actions">
                          <ng-template #tableActionsMenu let-close="close">
                            <button
                              type="button"
                              class="action-menu__item"
                              (click)="openProcedureQuickView(item); close()"
                            >
                              View
                            </button>
                            <button
                              type="button"
                              class="action-menu__item action-menu__item--primary"
                              (click)="openEdit(item); close()"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              class="action-menu__item"
                              [class.action-menu__item--danger]="item.isActive"
                              [class.action-menu__item--primary]="!item.isActive"
                              (click)="toggleActive(item); close()"
                            >
                              {{ item.isActive ? 'Archive' : 'Activate' }}
                            </button>
                          </ng-template>
                        </lib-action-menu>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <div class="procedure-types-page__cards">
              @for (item of pageItems; track item.id) {
                <article class="procedure-types-page__card-item" (click)="openProcedureQuickView(item)">
                  <div class="procedure-types-page__card-head">
                    <div>
                      <lib-hover-id [value]="item.id">
                        <div class="procedure-types-page__card-title">{{ item.name }}</div>
                      </lib-hover-id>
                      <div class="procedure-types-page__card-subtitle">
                        @if (item.deviceTypeId) {
                          <button
                            type="button"
                            class="procedure-types-page__link-button"
                            (click)="openDeviceTypeQuickView(item.deviceTypeId); $event.stopPropagation()"
                          >
                            <lib-hover-id [value]="item.deviceTypeId">
                              {{ deviceTypeName(item.deviceTypeId) }}
                            </lib-hover-id>
                          </button>
                        } @else {
                          No device required
                        }
                      </div>
                    </div>
                    <span
                      class="procedure-types-page__status-badge"
                      [class.procedure-types-page__status-badge--inactive]="!item.isActive"
                    >
                      {{ item.isActive ? ('Active' | t) : ('Inactive' | t) }}
                    </span>
                  </div>

                  <div class="procedure-types-page__card-grid">
                    <div class="procedure-types-page__card-row">
                      <span>Duration</span>
                      <strong>{{ item.defaultDurationMinutes }} min</strong>
                    </div>
                    <div class="procedure-types-page__card-row">
                      <span>Created</span>
                      <strong>{{ item.createdAt ? (item.createdAt | date: 'dd.MM.yyyy HH:mm') : '—' }}</strong>
                    </div>
                    <div class="procedure-types-page__card-row">
                      <span>Updated</span>
                      <strong>{{ item.updatedAt ? (item.updatedAt | date: 'dd.MM.yyyy HH:mm') : '—' }}</strong>
                    </div>
                  </div>

                  <div class="procedure-types-page__card-actions" (click)="$event.stopPropagation()">
                    <lib-action-menu [menuTemplate]="cardActionsMenu" buttonAriaLabel="Open procedure type actions">
                      <ng-template #cardActionsMenu let-close="close">
                        <button
                          type="button"
                          class="action-menu__item"
                          (click)="openProcedureQuickView(item); close()"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          class="action-menu__item action-menu__item--primary"
                          (click)="openEdit(item); close()"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          class="action-menu__item"
                          [class.action-menu__item--danger]="item.isActive"
                          [class.action-menu__item--primary]="!item.isActive"
                          (click)="toggleActive(item); close()"
                        >
                          {{ item.isActive ? 'Archive' : 'Activate' }}
                        </button>
                      </ng-template>
                    </lib-action-menu>
                  </div>
                </article>
              }
            </div>
          </section>
        </ng-template>
      }

      <ng-template #pageActionsMenu let-close="close">
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          [disabled]="isCompanyContextMissing"
          (click)="openCreateModal(); close()"
        >
          Create {{ procedureTypeLabelLower }}
        </button>
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          [disabled]="isCompanyContextMissing"
          (click)="openFiltersModal(); close()"
        >
          Filters & Sort (Ctrl+F)
        </button>
        @if (hasActiveFilters) {
          <button
            type="button"
            role="menuitem"
            class="action-menu__item"
            [disabled]="isCompanyContextMissing"
            (click)="resetFilters(); close()"
          >
            Reset
          </button>
        }
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          [disabled]="isCompanyContextMissing"
          (click)="refresh(); close()"
        >
          Refresh
        </button>
      </ng-template>

      <lib-floating-action-fab
        [menuTemplate]="pageActionsMenu"
        buttonAriaLabel="Open procedure type actions"
        [disabled]="isCompanyContextMissing"
      />

      @if (isFiltersModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          title="Filters & Sort"
          [subtitle]="'Tune the ' + procedureTypeLabelPluralLower + ' list'"
          width="680px"
          (close)="closeFiltersModal()"
        >
          <form
            [formGroup]="filtersForm"
            class="procedure-types-page__filters"
            (submit)="$event.preventDefault(); applyFilters()"
          >
            <label>
              Search
              <input
                type="text"
                formControlName="query"
                placeholder="Name, device type, UUID"
                data-modal-primary-focus="true"
              />
            </label>

            <label>
              Status
              <select formControlName="status">
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>

            <label>
              Device Type
              <select formControlName="deviceTypeId">
                <option value="">All</option>
                @for (deviceType of deviceTypes; track deviceType.id) {
                  <option [value]="deviceType.id">{{ deviceType.name }}</option>
                }
              </select>
            </label>

            <label>
              Sort
              <select formControlName="sort">
                <option value="createdAtDesc">Newest created</option>
                <option value="createdAtAsc">Oldest created</option>
                <option value="nameAsc">Name A-Z</option>
                <option value="nameDesc">Name Z-A</option>
                <option value="durationAsc">Shortest duration</option>
                <option value="durationDesc">Longest duration</option>
                <option value="statusDesc">Active first</option>
                <option value="statusAsc">Inactive first</option>
              </select>
            </label>

            <div class="procedure-types-page__modal-actions">
              <button type="button" class="procedure-types-page__action" (click)="resetFilters()">
                Reset
              </button>
              <button type="submit" class="procedure-types-page__submit">
                Apply Filters
              </button>
            </div>
          </form>
        </lib-page-action-modal>
      }

      @if (isCreateModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          [title]="'Create ' + procedureTypeLabelLower"
          subtitle="Add a company-level procedure catalog item."
          width="680px"
          (close)="closeCreateModal()"
        >
          <form [formGroup]="createForm" (ngSubmit)="create()" class="procedure-types-page__modal-grid">
            <label>
              Name
              <input type="text" formControlName="name" data-modal-primary-focus="true" />
            </label>

            <label>
              Device Type
              <select formControlName="deviceTypeId">
                <option value="">No device required</option>
                @for (deviceType of deviceTypes; track deviceType.id) {
                  <option [value]="deviceType.id">{{ deviceType.name }}</option>
                }
              </select>
            </label>

            <label>
              Duration (minutes)
              <input type="number" min="1" formControlName="defaultDurationMinutes" />
            </label>

            <div class="procedure-types-page__modal-actions">
              <button type="button" class="procedure-types-page__action" (click)="closeCreateModal()">Cancel</button>
              <button
                type="submit"
                class="procedure-types-page__submit"
                [disabled]="createForm.invalid || isCreating || isCompanyContextMissing"
              >
                {{ isCreating ? 'Creating...' : ('Create ' + procedureTypeLabelLower) }}
              </button>
            </div>
          </form>
        </lib-page-action-modal>
      }

      <lib-confirm-edit-modal
        [isOpen]="!!editingItem"
        [title]="'Edit ' + procedureTypeLabelLower"
        metaPrimaryLabel="Procedure ID"
        [metaPrimaryValue]="editingItem?.id ?? ''"
        confirmLabel="Confirm current name to unlock editing"
        confirmPlaceholder="Type current name"
        [confirmValue]="editConfirmName"
        (confirmValueChange)="onEditConfirmNameChange($event)"
        (cancel)="closeEdit()"
      >
        @if (editingItem) {
          <form [formGroup]="editForm" (ngSubmit)="saveEdit()" class="procedure-types-page__modal-grid">
            <label>
              Name
              <input type="text" formControlName="name" [disabled]="!isEditConfirmValid" data-modal-primary-focus="true" />
            </label>
            <label>
              Device Type
              <select formControlName="deviceTypeId" [disabled]="!isEditConfirmValid">
                <option value="">No device required</option>
                @for (deviceType of deviceTypes; track deviceType.id) {
                  <option [value]="deviceType.id">{{ deviceType.name }}</option>
                }
              </select>
            </label>
            <label>
              Duration (minutes)
              <input type="number" min="1" formControlName="defaultDurationMinutes" [disabled]="!isEditConfirmValid" />
            </label>

            <div class="procedure-types-page__modal-actions">
              <button type="button" class="procedure-types-page__action" (click)="closeEdit()">Cancel</button>
              <button
                type="submit"
                class="procedure-types-page__submit"
                [disabled]="!isEditConfirmValid || editForm.invalid || isSavingEdit"
              >
                {{ isSavingEdit ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </form>
        }
      </lib-confirm-edit-modal>

      @if (selectedProcedureQuickViewId) {
        <app-procedure-type-quick-view-modal
          [isOpen]="true"
          [procedureTypeId]="selectedProcedureQuickViewId"
          [companyIdOverride]="profileModalCompanyId"
          (close)="closeProcedureQuickView()"
        />
      }

      @if (selectedDeviceTypeQuickViewId) {
        <app-device-type-quick-view-modal
          [isOpen]="true"
          [deviceTypeId]="selectedDeviceTypeQuickViewId"
          [companyIdOverride]="profileModalCompanyId"
          title="Device Type Quick View"
          (close)="closeDeviceTypeQuickView()"
        />
      }
    </lib-page-shell>
  `,
  styles: [`
    .procedure-types-page__surface {
      display: grid;
      gap: 14px;
      padding: 16px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.94);
    }

    .procedure-types-page__surface-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
    }

    .procedure-types-page__surface-head h3 {
      margin: 0;
      color: #0f172a;
      font-size: 18px;
    }

    .procedure-types-page__surface-head p {
      margin: 4px 0 0;
      color: #475569;
      font-size: 13px;
    }

    .procedure-types-page__table-wrap {
      overflow-x: auto;
    }

    .procedure-types-page__table {
      width: 100%;
      min-width: 860px;
      border-collapse: collapse;
    }

    .procedure-types-page__table th,
    .procedure-types-page__table td {
      border-bottom: 1px solid #eef2f7;
      padding: 12px 10px;
      text-align: left;
      vertical-align: middle;
    }

    .procedure-types-page__table th {
      color: #0f172a;
      font-weight: 700;
      background: rgba(248, 250, 252, 0.9);
    }

    .procedure-types-page__table-row {
      cursor: pointer;
    }

    .procedure-types-page__table-row:hover {
      background: rgba(239, 246, 255, 0.6);
    }

    .procedure-types-page__entity-name {
      font-weight: 700;
      color: #0f172a;
    }

    .procedure-types-page__link-button {
      border: 0;
      padding: 0;
      background: transparent;
      color: #1d4ed8;
      font: inherit;
      text-align: left;
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .procedure-types-page__status-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 28px;
      padding: 0 10px;
      border-radius: 999px;
      background: #dcfce7;
      color: #166534;
      font-size: 12px;
      font-weight: 700;
    }

    .procedure-types-page__status-badge--inactive {
      background: #e5e7eb;
      color: #374151;
    }

    .procedure-types-page__actions-cell {
      width: 1%;
      white-space: nowrap;
    }

    .procedure-types-page__cards {
      display: none;
      gap: 12px;
    }

    .procedure-types-page__card-item {
      display: grid;
      gap: 12px;
      padding: 14px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
    }

    .procedure-types-page__card-item:hover {
      background: rgba(239, 246, 255, 0.6);
    }

    .procedure-types-page__card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
    }

    .procedure-types-page__card-title {
      color: #0f172a;
      font-size: 16px;
      font-weight: 700;
    }

    .procedure-types-page__card-subtitle {
      margin-top: 4px;
      color: #64748b;
      font-size: 13px;
    }

    .procedure-types-page__card-grid {
      display: grid;
      gap: 8px;
    }

    .procedure-types-page__card-row {
      display: grid;
      gap: 3px;
    }

    .procedure-types-page__card-row span {
      color: #64748b;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .procedure-types-page__card-row strong {
      color: #0f172a;
      font-size: 14px;
    }

    .procedure-types-page__card-actions {
      display: flex;
      justify-content: flex-end;
    }

    .procedure-types-page__filters,
    .procedure-types-page__modal-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
    }

    .procedure-types-page__filters label,
    .procedure-types-page__modal-grid label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      color: #334155;
      font-size: 13px;
      font-weight: 700;
    }

    .procedure-types-page__filters input,
    .procedure-types-page__filters select,
    .procedure-types-page__modal-grid input,
    .procedure-types-page__modal-grid select {
      min-height: 42px;
      box-sizing: border-box;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 0 12px;
      background: #fff;
      font: inherit;
    }

    .procedure-types-page__modal-actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }

    .procedure-types-page__submit,
    .procedure-types-page__action {
      min-height: 40px;
      border-radius: 8px;
      padding: 0 14px;
      border: 1px solid #d1d5db;
      background: #fff;
      color: #0f172a;
      font: inherit;
      font-weight: 700;
      cursor: pointer;
    }

    .procedure-types-page__submit {
      border-color: #2563eb;
      background: #2563eb;
      color: #fff;
    }

    .procedure-types-page__submit:disabled,
    .procedure-types-page__action:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    @media (max-width: 900px) {
      .procedure-types-page__table-wrap {
        display: none;
      }

      .procedure-types-page__cards {
        display: grid;
      }
    }

    @media (max-width: 640px) {
      .procedure-types-page__surface {
        padding: 12px;
      }

      .procedure-types-page__card-head,
      .procedure-types-page__modal-actions {
        flex-direction: column;
      }

      .procedure-types-page__submit,
      .procedure-types-page__action {
        width: 100%;
      }
    }
  `],
})
export class ProcedureTypesPageComponent implements OnInit, OnDestroy {
  private static readonly DEFAULT_PROCEDURE_TYPE_LABEL = 'Procedure';
  private static readonly PAGE_LIMIT = 20;

  private readonly fb = inject(FormBuilder);
  private readonly procedureTypesService = inject(ProcedureTypesService);
  private readonly deviceTypesService = inject(DeviceTypesService);
  private readonly settingsService = inject(SettingsService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();

  protected isLoading = true;
  protected isLoadingMore = false;
  protected hasMoreItems = false;
  protected isCreateModalOpen = false;
  protected isFiltersModalOpen = false;
  protected isCreating = false;
  protected isSavingEdit = false;
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected successText: string | null = null;
  protected procedureTypeLabel = ProcedureTypesPageComponent.DEFAULT_PROCEDURE_TYPE_LABEL;
  protected isSystemAdmin = false;
  protected items: ProcedureTypeItem[] = [];
  protected deviceTypes: DeviceTypeItem[] = [];
  protected editingItem: ProcedureTypeItem | null = null;
  protected editConfirmName = '';
  protected selectedProcedureQuickViewId: string | null = null;
  protected selectedDeviceTypeQuickViewId: string | null = null;

  private nextCursor: string | null = null;
  private appliedFilters: ProcedureTypeFilterState = this.getDefaultFilters();

  protected readonly createForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    deviceTypeId: [''],
    defaultDurationMinutes: [45, [Validators.required, Validators.min(1)]],
  });

  protected readonly editForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    deviceTypeId: [''],
    defaultDurationMinutes: [45, [Validators.required, Validators.min(1)]],
  });

  protected readonly filtersForm = this.fb.nonNullable.group({
    query: [''],
    status: this.fb.nonNullable.control<ProcedureTypeStatusFilter>(''),
    deviceTypeId: [''],
    sort: this.fb.nonNullable.control<ProcedureTypeListSort>('createdAtDesc'),
  });

  protected get procedureTypeLabelLower(): string {
    return this.lowerFirst(this.procedureTypeLabel);
  }

  protected get procedureTypeLabelPlural(): string {
    return this.toPlural(this.procedureTypeLabel);
  }

  protected get procedureTypeLabelPluralLower(): string {
    return this.lowerFirst(this.procedureTypeLabelPlural);
  }

  protected get isCompanyContextMissing(): boolean {
    return !this.hasRequiredCompanyContext();
  }

  protected get isEditConfirmValid(): boolean {
    const currentName = this.editingItem?.name.trim().toLowerCase() ?? '';
    return !!currentName && this.editConfirmName.trim().toLowerCase() === currentName;
  }

  protected get hasActiveFilters(): boolean {
    const defaults = this.getDefaultFilters();
    return this.appliedFilters.query !== defaults.query
      || this.appliedFilters.status !== defaults.status
      || this.appliedFilters.deviceTypeId !== defaults.deviceTypeId
      || this.appliedFilters.sort !== defaults.sort;
  }

  protected get profileModalCompanyId(): string | null {
    if (!this.isSystemAdmin) {
      return null;
    }

    return this.systemAdminContext.getCompanyId()?.trim() || null;
  }

  public ngOnInit(): void {
    this.isSystemAdmin = this.authService.getCurrentRole() === 'system_admin';

    if (this.isSystemAdmin) {
      this.subscriptions.add(
        this.systemAdminContext.companyId$.pipe(distinctUntilChanged()).subscribe(() => {
          this.closeProcedureQuickView();
          this.closeDeviceTypeQuickView();
          this.loadTerminology();
          this.refresh();
        }),
      );
      return;
    }

    this.loadTerminology();
    this.refresh();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected openCreateModal(): void {
    this.createForm.reset({ name: '', deviceTypeId: '', defaultDurationMinutes: 45 });
    this.actionErrorText = null;
    this.successText = null;
    this.isCreateModalOpen = true;
    this.ensureDeviceTypesLoaded();
  }

  protected closeCreateModal(): void {
    if (this.isCreating) {
      return;
    }

    this.isCreateModalOpen = false;
  }

  protected create(): void {
    if (!this.hasRequiredCompanyContext()) {
      this.actionErrorText = 'error.settingsCompanyContextRequired';
      return;
    }

    if (this.createForm.invalid) {
      return;
    }

    this.isCreating = true;
    this.actionErrorText = null;
    this.successText = null;

    this.procedureTypesService.create(this.createForm.getRawValue())
      .pipe(finalize(() => (this.isCreating = false)))
      .subscribe({
        next: () => {
          this.successText = `${this.procedureTypeLabel} created`;
          this.isCreateModalOpen = false;
          this.refresh();
        },
        error: (error) => {
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            fallback: 'error.generic',
          });
        },
      });
  }

  protected openFiltersModal(): void {
    this.filtersForm.reset({ ...this.appliedFilters });
    this.isFiltersModalOpen = true;
    this.ensureDeviceTypesLoaded();
  }

  protected closeFiltersModal(): void {
    this.isFiltersModalOpen = false;
  }

  protected applyFilters(): void {
    const value = this.filtersForm.getRawValue();
    this.appliedFilters = {
      query: value.query.trim(),
      status: value.status,
      deviceTypeId: value.deviceTypeId.trim(),
      sort: value.sort,
    };
    this.isFiltersModalOpen = false;
    this.refresh();
  }

  protected resetFilters(): void {
    const defaults = this.getDefaultFilters();
    this.appliedFilters = defaults;
    this.filtersForm.reset({ ...defaults });
    this.isFiltersModalOpen = false;
    this.refresh();
  }

  protected refresh(): void {
    this.nextCursor = null;
    this.hasMoreItems = false;
    this.loadPage(false);
  }

  protected loadMoreProcedureTypes(): void {
    if (!this.hasMoreItems || this.isLoadingMore) {
      return;
    }

    this.loadPage(true);
  }

  protected openEdit(item: ProcedureTypeItem): void {
    this.editingItem = item;
    this.editConfirmName = '';
    this.editForm.reset({
      name: item.name,
      deviceTypeId: item.deviceTypeId ?? '',
      defaultDurationMinutes: item.defaultDurationMinutes,
    });
    this.ensureDeviceTypesLoaded();
  }

  protected closeEdit(): void {
    this.editingItem = null;
    this.editConfirmName = '';
  }

  protected onEditConfirmNameChange(value: string): void {
    this.editConfirmName = value;
  }

  protected saveEdit(): void {
    if (!this.hasRequiredCompanyContext()) {
      this.actionErrorText = 'error.settingsCompanyContextRequired';
      return;
    }

    if (!this.editingItem || !this.isEditConfirmValid || this.editForm.invalid) {
      return;
    }

    const value = this.editForm.getRawValue();
    this.isSavingEdit = true;
    this.actionErrorText = null;
    this.successText = null;

    this.procedureTypesService
      .update(this.editingItem.id, {
        name: value.name.trim(),
        deviceTypeId: value.deviceTypeId.trim() ? value.deviceTypeId.trim() : null,
        defaultDurationMinutes: value.defaultDurationMinutes,
      })
      .pipe(finalize(() => (this.isSavingEdit = false)))
      .subscribe({
        next: () => {
          this.successText = `${this.procedureTypeLabel} updated`;
          this.editingItem = null;
          this.refresh();
        },
        error: (error) => {
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            fallback: 'error.generic',
          });
        },
      });
  }

  protected toggleActive(item: ProcedureTypeItem): void {
    if (!this.hasRequiredCompanyContext()) {
      this.actionErrorText = 'error.settingsCompanyContextRequired';
      return;
    }

    this.actionErrorText = null;
    this.successText = null;

    this.procedureTypesService.update(item.id, { isActive: !item.isActive }).subscribe({
      next: () => {
        this.successText = item.isActive
          ? `${this.procedureTypeLabel} archived`
          : `${this.procedureTypeLabel} activated`;
        this.refresh();
      },
      error: (error) => {
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      },
    });
  }

  protected openProcedureQuickView(item: ProcedureTypeItem): void {
    this.selectedProcedureQuickViewId = item.id;
  }

  protected closeProcedureQuickView(): void {
    this.selectedProcedureQuickViewId = null;
  }

  protected openDeviceTypeQuickView(deviceTypeId: string): void {
    this.selectedDeviceTypeQuickViewId = deviceTypeId;
  }

  protected closeDeviceTypeQuickView(): void {
    this.selectedDeviceTypeQuickViewId = null;
  }

  protected deviceTypeName(deviceTypeId: string | null): string {
    if (!deviceTypeId) {
      return 'No device required';
    }

    return this.deviceTypes.find((item) => item.id === deviceTypeId)?.name ?? 'Unknown device type';
  }

  private loadPage(append: boolean): void {
    if (!this.hasRequiredCompanyContext()) {
      this.isLoading = false;
      this.isLoadingMore = false;
      this.items = [];
      this.deviceTypes = [];
      this.errorText = 'error.settingsCompanyContextRequired';
      return;
    }

    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.errorText = null;
    }

    const page$ = this.procedureTypesService.getPage({
      companyId: this.profileModalCompanyId ?? undefined,
      limit: ProcedureTypesPageComponent.PAGE_LIMIT,
      cursor: append ? this.nextCursor : null,
      query: this.appliedFilters.query,
      status: this.appliedFilters.status,
      deviceTypeId: this.appliedFilters.deviceTypeId,
      sort: this.appliedFilters.sort,
    });

    const load$ = append
      ? page$.pipe(map((page): ProcedureTypeLoadResult => ({ page, deviceTypes: null })))
      : forkJoin({
        page: page$,
        deviceTypes: this.deviceTypesService.getList(this.profileModalCompanyId ?? undefined),
      });

    load$.pipe(finalize(() => {
      this.isLoading = false;
      this.isLoadingMore = false;
    })).subscribe({
      next: (result: ProcedureTypeLoadResult) => {
        if (result.deviceTypes) {
          this.deviceTypes = result.deviceTypes;
        }

        this.applyLoadedPage(result.page.items, result.page.nextCursor, result.page.hasMore, append);
      },
      error: (error: unknown) => {
        this.errorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      },
    });
  }

  private applyLoadedPage(
    items: ProcedureTypeItem[],
    nextCursor: string | null,
    hasMore: boolean,
    append: boolean,
  ): void {
    this.items = append ? [...this.items, ...items] : items;
    this.nextCursor = nextCursor;
    this.hasMoreItems = hasMore;
  }

  private loadTerminology(): void {
    if (!this.hasRequiredCompanyContext()) {
      this.procedureTypeLabel = ProcedureTypesPageComponent.DEFAULT_PROCEDURE_TYPE_LABEL;
      return;
    }

    this.settingsService.getOperationsSettings().subscribe({
      next: (settings) => {
        this.procedureTypeLabel = this.normalizeLabel(
          settings.procedureTypeLabel,
          ProcedureTypesPageComponent.DEFAULT_PROCEDURE_TYPE_LABEL,
        );
      },
      error: () => {
        this.procedureTypeLabel = ProcedureTypesPageComponent.DEFAULT_PROCEDURE_TYPE_LABEL;
      },
    });
  }

  private ensureDeviceTypesLoaded(): void {
    if (this.deviceTypes.length > 0 || !this.hasRequiredCompanyContext()) {
      return;
    }

    this.deviceTypesService.getList(this.profileModalCompanyId ?? undefined).subscribe({
      next: (items) => {
        this.deviceTypes = items;
      },
      error: () => {
        this.deviceTypes = [];
      },
    });
  }

  private getDefaultFilters(): ProcedureTypeFilterState {
    return {
      query: '',
      status: '',
      deviceTypeId: '',
      sort: 'createdAtDesc',
    };
  }

  private normalizeLabel(value: string | null | undefined, fallback: string): string {
    const normalized = (value ?? '').trim().replace(/\s+/g, ' ');
    return normalized || fallback;
  }

  private lowerFirst(value: string): string {
    return value ? value.charAt(0).toLowerCase() + value.slice(1) : value;
  }

  private toPlural(value: string): string {
    if (!value) {
      return value;
    }

    const lower = value.toLowerCase();
    if (lower.endsWith('s')) {
      return value;
    }

    return `${value}s`;
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }
}
